import type { ClientPositionView } from "@solomon-apps/shared";
import { formatPercent } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { formatObservedUsdv } from "@/hooks/positionAmounts";
import type { ClientDashboard } from "@/types/clientDashboard";

import { groupHoldings } from "../Positions/holdings";
import { positionRewardRate } from "../Positions/positionRewardMetrics";

/** Shown in a key-stat cell when the figure has no real source. */
export const UNAVAILABLE = "—";
/** Shown in a key-stat cell while its source is still loading. */
export const LOADING = "...";

/** A key-stat value plus the on-demand hint that explains it. */
export interface StatReading {
    value: string;
    hint: string;
}

/* ── Hint copy (tooltips: complete sentences, 10-20 words) ── */

export const CURRENT_APY_HINT = "The USDv rate Solomon advertises today, based on the latest 7-day staking yield.";

export const HISTORICAL_APY_HINT =
    "Realized over the last 30 days: USDv distributed to your accounts over their average daily balance, annualized.";

export const PENDING_HINT = "Pending rewards are unavailable because the settlement history is incomplete.";

const HISTORICAL_NO_DISTRIBUTIONS = "No rewards have been distributed to your accounts yet.";
const HISTORICAL_NO_BALANCES = "Your accounts have no balance history in the last 30 days.";
const HISTORICAL_FAILED = "It could not be loaded. Try again later.";

/**
 * An APY percentage (already in percent units, e.g. `"10.64"` or `10.64`) as
 * the data guide's 1-decimal percent (`"10.6%"`). Anything that is not a
 * finite number (null, `""`, `"abc"`) reads as unavailable. String inputs are
 * rounded in BigDecimal, never through a float.
 */
export function formatApyPct(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return UNAVAILABLE;
    if (typeof value === "number") {
        return Number.isFinite(value) ? formatPercent(value, 1) : UNAVAILABLE;
    }
    const trimmed = value.trim();
    if (trimmed === "") return UNAVAILABLE;
    try {
        const decimal = new BigDecimal(trimmed);
        return decimal.isNaN() ? UNAVAILABLE : formatPercent(decimal, 1);
    } catch {
        return UNAVAILABLE;
    }
}

/** Current APY: the protocol's advertised rate (`useAdvertisedApy`). */
export function resolveCurrentApy(args: {
    apy: number | null;
    isLoading: boolean;
    positions?: ClientPositionView[];
}): StatReading {
    if (args.apy === null && args.positions?.some((position) => position.rewardMetrics)) {
        const rate = positionRewardRate(
            groupHoldings(args.positions).map((holding) => holding.positions),
            "current"
        );
        if (rate !== null)
            return {
                value: formatApyPct(rate.toString()),
                hint: "Annualized rate from your latest completed settlement period. This rate is not a forecast."
            };
    }
    if (args.isLoading) return { value: LOADING, hint: CURRENT_APY_HINT };
    return { value: formatApyPct(args.apy), hint: CURRENT_APY_HINT };
}

/**
 * Prefer paid settlement-period rewards and their average balances. The
 * dashboard's daily-balance rate supports API versions without this evidence.
 */
export function resolveHistoricalApy(args: {
    dashboard: ClientDashboard | null;
    isLoading: boolean;
    error: Error | null;
    positions?: ClientPositionView[];
}): StatReading {
    const { dashboard, isLoading, error } = args;
    if (args.positions?.some((position) => position.rewardMetrics)) {
        const rate = positionRewardRate(
            groupHoldings(args.positions).map((holding) => holding.positions),
            "paid"
        );
        if (rate !== null)
            return {
                value: formatApyPct(rate.toString()),
                hint: "Paid rewards for completed settlement periods in the last 30 days, divided by average balance and annualized."
            };
    }
    if (isLoading && !dashboard) return { value: LOADING, hint: HISTORICAL_APY_HINT };
    if (!dashboard) {
        return {
            value: UNAVAILABLE,
            hint: error ? `${HISTORICAL_APY_HINT} ${HISTORICAL_FAILED}` : HISTORICAL_APY_HINT
        };
    }
    if (!dashboard.lastDistributionAt) {
        return { value: UNAVAILABLE, hint: `${HISTORICAL_APY_HINT} ${HISTORICAL_NO_DISTRIBUTIONS}` };
    }
    const value = formatApyPct(dashboard.trailingApy?.thirtyDay ?? null);
    if (value === UNAVAILABLE) {
        return { value, hint: `${HISTORICAL_APY_HINT} ${HISTORICAL_NO_BALANCES}` };
    }
    return { value, hint: HISTORICAL_APY_HINT };
}

/** Sum recorded entitlement less finalized payments across the positions. */
export function resolvePendingRewards(positions?: ClientPositionView[]): StatReading {
    if (!positions?.length || positions.some((position) => (position.rewardMetrics?.pendingRewards ?? null) === null)) {
        return { value: UNAVAILABLE, hint: PENDING_HINT };
    }
    const total = positions.reduce(
        (sum, position) => sum.add(position.rewardMetrics!.pendingRewards!),
        new BigDecimal(0)
    );
    return {
        value: formatObservedUsdv(total),
        hint: "Computed rewards that have not yet been paid. This excludes rewards from unfinished settlement periods."
    };
}
